import { forwardRef, useEffect } from 'react';
import { MenuPlate } from '../MenuPlate';
import { NoResults } from '../NoResults';
import { Product } from '../../../../interfaces/domain/menu';
import { useMenuPlates } from '../../../../hooks/useMenuPlates';
import styles from './MenuPlates.module.scss';
import { useCategoriesStore, useOrdersStore, useUIStore } from '../../../../store';

export const MenuPlates = forwardRef<HTMLDivElement>((_, ref) => {
  const { categoriesToDisplay, isEmpty } = useMenuPlates();


  const showSearch = useUIStore((s) => s.showSearch);

  const canUseCart = useOrdersStore((s) => s.canUseCart);

  const setCategorySelected = useCategoriesStore(
    (s) => s.setCategorySelected
  );

  // 🔥 SCROLL → sincroniza categoría activa
  useEffect(() => {
    const sections = document.querySelectorAll("section[id^='cat-']");
    const categoriesBar = document.querySelector<HTMLElement>('[data-sticky-categories-bar]');

    const handleScroll = () => {
      const { isManualScrolling } =
        useCategoriesStore.getState();

      if (isManualScrolling) return;

      // 🔥 si estamos en búsqueda, no sincronizamos categoría
      if (showSearch) {
        setCategorySelected(null);
        return;
      }

      // Borde inferior de la barra sticky en el viewport = threshold exacto
      const STICKY_OFFSET = categoriesBar
        ? categoriesBar.getBoundingClientRect().bottom
        : 128;

      if (window.scrollY < 50) {
        setCategorySelected(null);
        return;
      }

      let currentId = null;
      let currentName = "";

      const isAtBottom =
        window.innerHeight + window.scrollY >=
        document.body.offsetHeight - 10;

      sections.forEach((section) => {
        const rect = section.getBoundingClientRect();

        if (rect.top <= STICKY_OFFSET) {
          currentId = section.id.replace("cat-", "");
          currentName =
            section.getAttribute("data-name") || "";
        }

        if (isAtBottom) {
          currentId = section.id.replace("cat-", "");
          currentName =
            section.getAttribute("data-name") || "";
        }
      });

      if (currentId) {
        setCategorySelected({
          id: currentId,
          name: currentName,
        });
      }
    };

    window.addEventListener("scroll", handleScroll);

    return () =>
      window.removeEventListener("scroll", handleScroll);
  }, [setCategorySelected, showSearch]);


  // 🔥 SCROLL AUTOMÁTICO AL ACTIVAR BÚSQUEDA (Versión Robusta)
  useEffect(() => {
    if (showSearch) {
      // 1. Esperamos un poco más para que los resultados filtren
      const timeout = setTimeout(() => {
        // 2. Buscamos el contenedor por su clase (usa la que genera tu SCSS)
        const container = document.querySelector(`.${styles.containerMenuPlatesFormSearch}`);

        if (container) {
          // 3. Calculamos la distancia
          const STICKY_OFFSET = 120; // Ajusta este valor según el alto de tu buscador
          const elementPosition = container.getBoundingClientRect().top + window.scrollY;

          window.scrollTo({
            top: elementPosition - STICKY_OFFSET,
            behavior: 'smooth',
          });
        } else {
          // Si no encuentra el contenedor de búsqueda, probamos con el genérico
          const fallback = document.querySelector(`.${styles.container}`);
          if (fallback) {
            fallback.scrollIntoView({ behavior: 'smooth', block: 'start' });
          }
        }
      }, 150); // Un pelín más de tiempo para asegurar el render

      return () => clearTimeout(timeout);
    }
  }, [showSearch]);


  if (isEmpty) {
    return (
      <div className={styles.containerNoResults}>
        <NoResults />
      </div>
    );
  }


  return (
    <div className={`${showSearch ? styles.containerMenuPlatesFormSearch : canUseCart ? styles.containerMenuPlatesWithTotal : styles.containerMenuPlatesWithOutTotal}`} ref={ref}>
      {categoriesToDisplay.map((category) => (
        <section
          key={category.id}
          id={`cat-${category.id}`}
          data-name={category.name}
          className={styles.scrollTarget}
        >
          <h2 className={styles.nameCategory}>
            {category.name}
          </h2>
          {category.description && (
            <p className={styles.categoryDescription}>
              {category.description}
            </p>
          )}

          {!!category.products?.length && (
            <div className={styles.gridWrapper}>
              {category.products.map((plate: Product) => (
                <div
                  key={plate.id}
                  className={styles.plateBox}
                >
                  <MenuPlate plate={plate} />
                </div>
              ))}
            </div>
          )}
        </section>
      ))}
    </div>
  );
});

MenuPlates.displayName = 'MenuPlates';